import React, { useEffect, useState } from 'react';
import { Navbar, createStyles, rem, Tooltip, UnstyledButton, Box, Stack, useMantineColorScheme } from '@mantine/core';
import { IconBuilding, IconCategory, IconChartArrowsVertical, IconReportMoney, IconSunFilled, IconMoonFilled, IconUser, IconLogout2, IconHome, IconMedal, IconNews, IconUserCode, IconUserDollar, IconUsers, IconBellCog, IconPuzzle, IconTie, IconSettings, IconUserCog, IconMapPin, IconHeartHandshake, IconSlideshow } from '@tabler/icons-react';
import { useDispatch, useSelector } from 'react-redux';
import { notifications } from '@mantine/notifications';
import { useLocation, useNavigate } from 'react-router-dom';
import useUserApi from '../../hooks/useUserApi';

// Custom styles
const useStyles = createStyles((theme) => ({
	wrapper: {
		display: 'flex',
		padding: 10,
		backgroundColor: 'transparent',
		border: 0,
	},
	aside: {
		flex: `0 0 ${rem(60)}`,
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'center',
		paddingTop: 10,
		paddingBottom: 10,
		backgroundColor: theme.colors.mainPurple[4],
		borderRadius: 10,
		overflowY: 'auto',
		maxHeight: '100vh',
	},
	mainLink: {
		width: rem(44),
		height: rem(44),
		borderRadius: theme.radius.md,
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		color: '#FFFFFF',
		'&:hover': {
			backgroundColor: theme.colors.mainPurple[1],
		},
	},
	mainLinkActive: {
		'&, &:hover': {
			backgroundColor: theme.colors.mainPurple[1],
			color: '#FFFFFF',
		},
	},
}));

const AppSidebar = () => {

	// State and hooks
	const { classes, cx } = useStyles();
	const [active, setActive] = useState('Home');
	const { colorScheme, toggleColorScheme } = useMantineColorScheme();
	const dark = colorScheme === 'dark';
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const location = useLocation();
	const { verifyJWT } = useUserApi();
	const { tenant } = useSelector(state => state.authData);

	// On load
	useEffect(() => {
		async function fetchData() {
			await verifyJWT();
		}
		fetchData();
		// eslint-disable-next-line
	}, []);

	// On load
	useEffect(() => {

		// Split current path
		var cr = location.pathname.split('/')[1];

		// Assign state depending on route
		if (cr === '') {

			// Assign home
			setActive('Home');

		} else {

			// Filter routes
			var filter = sections.filter(el => el.to.split('/')[1] === cr);

			// Assign state
			setActive(filter[0]?.label);

		}

		// eslint-disable-next-line
	}, []);

	// Active menu item
	const activeItem = (label, href) => {

		// Set active in state and navigate
		setActive(label);
		navigate(href);

	}

	// Sections array
	const sections = !tenant ? [
		{ icon: <IconHome size="1.2rem" stroke={1.5} />, label: 'Home', to: '/' },
		{ icon: <IconChartArrowsVertical size="1.2rem" stroke={1.5} />, label: 'Niveles', to: '/levels' },
		{ icon: <IconReportMoney size="1.2rem" stroke={1.5} />, label: 'Partners', to: '/partners' },
		{ icon: <IconCategory size="1.2rem" stroke={1.5} />, label: 'Categorías', to: '/categories' },
		{ icon: <IconBuilding size="1.2rem" stroke={1.5} />, label: 'Sucursales', to: '/branches' },
		{ icon: <IconMedal size="1.2rem" stroke={1.5} />, label: 'Beneficios', to: '/benefits' },
		{ icon: <IconNews size="1.2rem" stroke={1.5} />, label: 'Noticias', to: '/posts' },
		{ icon: <IconUserCode size="1.2rem" stroke={1.5} />, label: 'Usuarios Beta', to: '/beta_users' },
		{ icon: <IconUserDollar size="1.2rem" stroke={1.5} />, label: 'Usuarios Partners', to: '/partner_users' },
		{ icon: <IconUsers size="1.2rem" stroke={1.5} />, label: 'Usuarios', to: '/users' },
		{ icon: <IconBellCog size="1.2rem" stroke={1.5} />, label: 'Notificaciones', to: '/notifications' },
		{ icon: <IconPuzzle size="1.2rem" stroke={1.5} />, label: 'Desafíos', to: '/challenges' },
		{ icon: <IconMapPin size="1.2rem" stroke={1.5} />, label: 'Ciudades Participantes', to: '/cities' },
		{ icon: <IconHeartHandshake size="1.2rem" stroke={1.5} />, label: 'Con el apoyo de', to: '/supports' },
		{ icon: <IconSlideshow size="1.2rem" stroke={1.5} />, label: 'Banner In-App', to: '/banner_in_app' },
		{ icon: <IconTie size="1.2rem" stroke={1.5} />, label: 'Clientes Corporativos', to: '/corporate_partners' },
		{ icon: <IconUserCog size="1.2rem" stroke={1.5} />, label: 'Administradores Corporativos', to: '/corporate_admins' },
	] : [
		{ icon: <IconHome size="1.2rem" stroke={1.5} />, label: 'Home', to: '/' },
		{ icon: <IconUsers size="1.2rem" stroke={1.5} />, label: 'Usuarios', to: '/tenant_users' },
		{ icon: <IconBuilding size="1.2rem" stroke={1.5} />, label: 'Ubicaciones', to: '/locations' },
		{ icon: <IconMedal size="1.2rem" stroke={1.5} />, label: 'Beneficios', to: '/benefits' },
		{ icon: <IconBellCog size="1.2rem" stroke={1.5} />, label: 'Notificaciones', to: '/notifications' },
		{ icon: <IconPuzzle size="1.2rem" stroke={1.5} />, label: 'Desafíos', to: '/challenges' },
		{ icon: <IconNews size="1.2rem" stroke={1.5} />, label: 'Noticias', to: '/posts' },
		{ icon: <IconSettings size="1.2rem" stroke={1.5} />, label: 'Configuración', to: '/settings' },
	];

	// Build section links
	const sectionLinks = sections.map((link) => (
		<Tooltip
			label={link.label}
			position="right"
			withArrow
			transitionProps={{ duration: 0 }}
			key={link.label}
		>
			{/* Button */}
			<UnstyledButton
				onClick={() => activeItem(link.label, link.to)}
				className={cx(classes.mainLink, { [classes.mainLinkActive]: link.label === active })}
			>
				{/* Icon */}
				{link.icon}
			</UnstyledButton>
		</Tooltip>
	));

	// Logout
	const logout = () => {

		// Delete entry in localstorage
		localStorage.removeItem('webikecc_jwt');

		// Reset auth state
		dispatch.authData.resetState();

		// Show message
		notifications.show({
			title: 'Sesión finalizada',
			message: 'Tu sesión se ha finalizado correctamente.',
			color: 'mainPurple'
		});

		// Navigate to login
		navigate('/');

	}

	return (
		<Navbar
			width={{ base: 80 }}
			className={classes.wrapper}
		>
			<Navbar.Section
				grow
				className={classes.aside}
			>
				<Stack
					spacing={0}
					justify="space-between"
					h="100%"
				>
					{/* Section Links */}
					<Box>
						{sectionLinks}
					</Box>
					{/* Options */}
					<Box>
						{/* Theme */}
						<Tooltip
							label={`Cambiar a modo ${dark ? 'claro' : 'oscuro'}`}
							position="right"
							withArrow
							transitionProps={{ duration: 0 }}
						>
							{/* Button */}
							<UnstyledButton
								className={classes.mainLink}
								onClick={() => toggleColorScheme()}
							>
								{/* Icon */}
								{dark ?
									<IconSunFilled size="1.2rem" stroke={1.5} />
									:
									<IconMoonFilled size="1.2rem" stroke={1.5} />
								}
							</UnstyledButton>
						</Tooltip>
						{/* Account */}
						<Tooltip
							label="Cuenta y preferencias"
							position="right"
							withArrow
							transitionProps={{ duration: 0 }}
						>
							{/* Button */}
							<UnstyledButton className={classes.mainLink}>
								{/* Icon */}
								<IconUser size="1.2rem" stroke={1.5} />
							</UnstyledButton>
						</Tooltip>
						{/* Logout */}
						<Tooltip
							label="Cerrar sesión"
							position="right"
							withArrow
							transitionProps={{ duration: 0 }}
						>
							{/* Button */}
							<UnstyledButton
								className={classes.mainLink}
								onClick={logout}
							>
								{/* Icon */}
								<IconLogout2 size="1.2rem" stroke={1.5} />
							</UnstyledButton>
						</Tooltip>
					</Box>
				</Stack>
			</Navbar.Section>
		</Navbar>
	);

}

export default AppSidebar;
